import React, { useRef, useState } from 'react';
import './input.css';

// Eye / eye-off glyph used by the password reveal toggle.
const EyeIcon = ({ off }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {off ? (
            <>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
            </>
        ) : (
            <>
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                <circle cx="12" cy="12" r="3" />
            </>
        )}
    </svg>
);

const Input = ({
    classList,
    required = false,
    errorMessage,
    errorMessageStatus,
    placeholder,
    children,
    label,
    type = null,
    forRef = null,
    autoFocus,
    noPasswordToggle = false,
    ...restProps
}) => {
    const inputRef = useRef(null);
    const [revealed, setRevealed] = useState(false);

    // "secret" looks and behaves like a password field (masked + reveal toggle) but is
    // never a real <input type="password">, so browsers' save-password managers (which key
    // off that native type) don't mistake it for a login credential -- used for API keys,
    // which aren't a login password and shouldn't prompt "save password for this site?".
    const isSecret = type === 'secret';
    // A password field gets a built-in reveal toggle unless it's explicitly
    // opted out. When revealed we swap the native type to "text".
    const hasPasswordToggle = (type === 'password' || isSecret) && !noPasswordToggle;
    const resolvedType = isSecret
        ? 'text'
        : hasPasswordToggle
            ? (revealed ? 'text' : 'password')
            : (type != null ? type : 'text');

    let formID = `${{ ...restProps }?.name}-${Math.floor(Math.random() * 1000)}`;

    const inputEl = (
        <input
            type={resolvedType}
            placeholder={placeholder ?? ''}
            required={required}
            className={`${type == 'checkbox' ? 'form-check-input' : 'form-input'} ${
                !!errorMessageStatus ? 'has-error' : ''
            }${hasPasswordToggle ? ' has-password-toggle' : ''}`}
            id={formID}
            ref={forRef ?? inputRef}
            style={isSecret && !revealed ? { WebkitTextSecurity: 'disc' } : undefined}
            {...restProps}
            autoFocus={autoFocus}
        />
    );

    return (
        <div className={`${type == 'checkbox' ? 'form-check' : 'input-groups'} ${classList != undefined ? classList : ''}`}>
            <div className={`${type != 'checkbox' ? 'form-input' : 'd-flex'}`}>
                {label && <label className="control-label" htmlFor={formID}>{required && '*'} {label}</label>}
                {hasPasswordToggle ? (
                    <div className="password-input">
                        {inputEl}
                        <button
                            type="button"
                            className="password-input__toggle"
                            onClick={() => setRevealed((v) => !v)}
                            tabIndex={-1}
                            aria-label={revealed ? 'Hide password' : 'Show password'}
                            title={revealed ? 'Hide password' : 'Show password'}
                        >
                            <EyeIcon off={revealed} />
                        </button>
                    </div>
                ) : (
                    inputEl
                )}
                {children}
                <div className='input-messages'>
                    {errorMessage}
                </div>
            </div>
        </div>
    );
};
export default Input;
